import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from "axios";
import {Button, TextField} from "@mui/material";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {ObjectSectionSelect} from "../../components/Select/domainSelects";
import ObjectSubSectionAutocomplete from "../../components/Autocompletes/ObjectSubSectionAutocomplete";
import useValidations from "../../hooks/useValidations";
import Typography from "@mui/material/Typography";
import useLoader from "../../hooks/useLoader";
import LoadingButton from "@mui/lab/LoadingButton";
import {anObjectsCRUD} from "../../http/CRUD";
import CheckboxField from "../../components/CheckboxField";
import {useDispatch, useSelector} from "react-redux";
import {getAnObjectSubSections} from "../../redux/actions/anObjectSubSectionActions";

export default function CreateObjectForm({afterSubmit, onClose}) {
  const dispatch = useDispatch();
  const [data, setData] = useState({});
  const [gbdLoading, setGbdLoading] = useState(false);
  const [gbdError, setGbdError] = useState(null);
  const [gbdAutofilled, setGbdAutofilled] = useState(false);
  const [lastBinFetched, setLastBinFetched] = useState(null);
  const [gbdVerifiedBins, setGbdVerifiedBins] = useState(() => new Set());
  const [gbdStatus, setGbdStatus] = useState("idle"); // idle | loading | verified | not_found | error
  const [isIpChecked, setIsIpChecked] = useState(false);

  const normalizedBin = useMemo(() => String(data.bin || "").replace(/\D/g, ""), [data.bin]);

  const esbInfoByBinUrl = useMemo(
    () =>
      process.env.REACT_APP_ESB_UL_INFO_BY_BIN_URL ||
      "https://ekpo.nce.kz/esb/api/v1/shepEsb/ul/infoByBin",
    []
  );

  const requestorBin = useMemo(
    () => process.env.REACT_APP_ESB_REQUESTOR_BIN || "150740006678",
    []
  );

  const handleChange = useCallback((key, value) => {
    setSubmitErrors(false);
    setGbdError(null);
    const newData = {...data, [key]: value};
    // При изменении раздела сбрасываем подраздел и подподраздел
    if (key === "sectionId" && value !== data.sectionId) {
      newData.subSectionId = null;
      newData.subSubSectionId = null;
    }
    if (key === "subSectionId" && value !== data.subSectionId) {
      newData.subSubSectionId = null;
    }
    if (key === "addressLine") {
      // пользователь руками поправил адрес — больше не перезатираем автозаполнением
      setGbdAutofilled(false);
    }
    setData(newData);
  }, [data]);


  const extractGbdUlPayload = useCallback((response) => {
    if (typeof response === "string") {
      // ESB может вернуть чистую XML-строку без обёртки
      return { xml: response };
    }

    const directOrg =
      response?.response?.responseData?.Organization ||
      response?.responseData?.Organization ||
      response?.responseData?.Organization ||
      response?.Organization ||
      null;
    const directAddress =
      directOrg?.Address || response?.Address || response?.organization?.address || null;

    if (directAddress) {
      return {
        shortNameRu: directOrg?.ShortNameRu || directOrg?.shortNameRu || null,
        fullNameRu: directOrg?.FullNameRu || directOrg?.fullNameRu || null,
        address: {
          districtRu: directAddress?.DistrictRu || directAddress?.districtRu || null,
          regionRu: directAddress?.RegionRu || directAddress?.regionRu || null,
          cityRu: directAddress?.CityRu || directAddress?.cityRu || null,
          streetRu: directAddress?.StreetRu || directAddress?.streetRu || null,
          buildingNumber: directAddress?.BuildingNumber || directAddress?.buildingNumber || null,
          zipCode: directAddress?.ZIPCode || directAddress?.zipCode || null,
        },
      };
    }

    const xmlLike =
      response?.response?.responseData?.data ||
      response?.responseData?.data ||
      response?.responseData?.Data ||
      response?.responseData ||
      response?.data ||
      null;
    if (typeof xmlLike !== "string" || !xmlLike.includes("<")) {
      return null;
    }

    try {
      const doc = new DOMParser().parseFromString(xmlLike, "text/xml");
      const pick = (tag) => doc.getElementsByTagName(tag)?.[0]?.textContent || null;
      const pickIn = (parentTag, childTag) => {
        const parent = doc.getElementsByTagName(parentTag)?.[0];
        if (!parent) return null;
        return parent.getElementsByTagName(childTag)?.[0]?.textContent || null;
      };
      const statusCode = pick("Code");
      const orgBin = pick("BIN");
      const hasOrganization = Boolean(orgBin);
      const shortNameRu = pick("ShortNameRu");
      const fullNameRu = pick("FullNameRu");

      const address = {
        districtRu: pick("DistrictRu"),
        regionRu: pick("RegionRu"),
        cityRu: pick("CityRu"),
        streetRu: pick("StreetRu"),
        buildingTypeRu: pickIn("BuildingType", "NameRu"),
        buildingNumber: pick("BuildingNumber"),
        zipCode: pick("ZIPCode"),
      };
      const hasAnyAddressPart = Object.values(address).some(
        (v) => v != null && String(v).trim() !== "" && String(v).trim() !== "-"
      );

      return {
        statusCode,
        orgBin,
        found: statusCode === "002" || hasOrganization,
        shortNameRu,
        fullNameRu,
        address: hasAnyAddressPart ? address : null,
        xml: xmlLike,
      };
    } catch {
      return null;
    }
  }, []);

  const buildAddressLine = useCallback((address) => {
    if (!address) return "";
    const parts = [
      address.districtRu,
      address.regionRu && address.regionRu !== "-" ? address.regionRu : null,
      address.cityRu && address.cityRu !== "-" ? address.cityRu : null,
      address.streetRu,
    ].filter((x) => x != null && String(x).trim() !== "");

    const streetPart = parts.join(", ");
    const b = String(address.buildingNumber || "").trim();
    const bt = String(address.buildingTypeRu || "").trim();
    const zip = String(address.zipCode || "").trim();
    const buildingPart = [bt || null, b || null].filter(Boolean).join(" ").trim();
    const tail = [buildingPart || null, zip || null].filter(Boolean).join(", ");
    return [streetPart, tail].filter(Boolean).join(", ");
  }, []);

  const fetchGbdUlByBin = useCallback(async (bin) => {
    const clean = String(bin || "").replace(/\D/g, "");
    if (clean.length !== 12) return;
    if (clean === lastBinFetched) return;

    try {
      setGbdLoading(true);
      setGbdError(null);
      setGbdStatus("loading");
      const token = localStorage.getItem("token");
      const csrf = localStorage.getItem("csrfToken");
      const resp = await axios
        .post(
          esbInfoByBinUrl,
          {
            RequestorBIN: requestorBin,
            bin: clean,
          },
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : null),
              ...(csrf ? { "X-XSRF-TOKEN": csrf } : null),
              "Content-Type": "application/json",
            },
            // ESB иногда отвечает очень долго; чтобы UI не зависал, ограничиваем ожидание.
            timeout: 20000,
          }
        )
        .then((r) => r.data);
      const extracted = extractGbdUlPayload(resp);
      // Если ESB вернул чистую XML-строку — парсим из неё
      const parsed =
        extracted?.xml && !extracted?.found && !extracted?.address
          ? extractGbdUlPayload({ responseData: { data: extracted.xml } })
          : extracted;

      if (!parsed?.found) {
        setLastBinFetched(clean);
        setGbdError("ГБД ЮЛ: организация по БИН не найдена");
        setGbdStatus("not_found");
        setGbdVerifiedBins((prev) => {
          const next = new Set(prev);
          next.delete(clean);
          return next;
        });
        return;
      }

      if (parsed?.address) {
        const nextAddressLine = buildAddressLine(parsed.address);
        setData((prev) => {
          const shouldFillAddress = !prev.addressLine || gbdAutofilled;
          const shouldFillName = !prev.name || String(prev.name).trim() === "";
          const nextName =
            String(parsed?.shortNameRu || "").trim() ||
            String(parsed?.fullNameRu || "").trim() ||
            null;
          const shouldMarkSource = !prev.rpnId || String(prev.rpnId).trim() === "";
          return {
            ...prev,
            // Для ЮЛ по ГБД ЮЛ логично выставить тип "Субъект" (2), если пользователь не трогал
            ...(prev.type == null ? { type: 2 } : null),
            ...(shouldFillName && nextName ? { name: nextName } : null),
            ...(shouldFillAddress ? { addressLine: nextAddressLine || prev.addressLine } : null),
            // Метка источника, чтобы в дальнейшем фильтровать "новые правильные" объекты
            ...(shouldMarkSource ? { rpnId: "GBDUL" } : null),
          };
        });
      }

      setGbdAutofilled(true);
      setLastBinFetched(clean);
      setGbdStatus("verified");
      setGbdVerifiedBins((prev) => {
        const next = new Set(prev);
        next.add(clean);
        return next;
      });
    } catch (e) {
      const msg = String(e || "Ошибка ГБД ЮЛ");
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(msg);
      setGbdError(isTimeout ? "ГБД ЮЛ: время ожидания проверки истекло" : msg);
      setGbdStatus("error");
      setGbdVerifiedBins((prev) => {
        const next = new Set(prev);
        next.delete(clean);
        return next;
      });
    } finally {
      setGbdLoading(false);
    }
  }, [buildAddressLine, extractGbdUlPayload, gbdAutofilled, lastBinFetched]);

  useEffect(() => {
    if (!normalizedBin || normalizedBin.length !== 12) return;
    setGbdStatus("idle");
    const t = setTimeout(() => {
      fetchGbdUlByBin(normalizedBin);
    }, 600);
    return () => clearTimeout(t);
  }, [fetchGbdUlByBin, isIpChecked, normalizedBin]);

  const {anObjectSubSections, anObjectSubSectionRequestedCount} = useSelector(
    (state) => state.anObjectSubSection
  );

  useEffect(() => {
    if (anObjectSubSectionRequestedCount > 0) return;
    dispatch(
      getAnObjectSubSections({
        paging: {skip: 0},
        sort: {name: {operator: "asc"}},
      })
    );
  }, [anObjectSubSectionRequestedCount, dispatch]);

  /** L3: AnObjectSubSection с parentId = выбранный подраздел */
  const dictSubSubSections = useMemo(() => {
    if (!data.subSectionId) return [];
    return (anObjectSubSections || []).filter(
      (opt) => Number(opt.parentId) === Number(data.subSectionId)
    );
  }, [anObjectSubSections, data.subSectionId]);

  const hasDictSubSubSections = dictSubSubSections.length > 0;
  // Поле показываем после выбора подраздела; без сида справочника список будет пустым
  const showSubSubSection = Boolean(data.subSectionId);

  const validationRules = useMemo(() => {
    const rules = [
      {label: "2.1 Наименование объекта", code: "name", rules: ["required"]},
      {label: "2.2 БИН/ИИН объекта", code: "bin", rules: ["required"]},
      {label: "2.4 Адрес", code: "addressLine", rules: ["required"]},
      {label: "3.1 Раздел объекта", code: "sectionId", rules: ["required"]},
      {label: "3.2 Подраздел объекта", code: "subSectionId", rules: ["required"]},
    ];
    // Обязателен только если в справочнике уже есть дети у подраздела
    if (showSubSubSection && hasDictSubSubSections) {
      rules.push({
        label: "3.3 Подподраздел объекта",
        code: "subSubSectionId",
        rules: ["required"],
      });
    }
    rules.push({
      label: showSubSubSection ? "3.4 Объект" : "3.3 Объект",
      code: "description",
      rules: ["required"],
    });
    return rules;
  }, [hasDictSubSubSections, showSubSubSection]);

  const {isValid, renderedErrors, fieldErrors} = useValidations(data, validationRules);

  const [showSubmitErrors, setShowSubmitErrors] = useState(null);
  const [submitErrors, setSubmitErrors] = useState(null);

  const {loading, start, stop} = useLoader(false);
  const submit = useCallback(async () => {
    const cleanBin = String(data.bin || "").replace(/\D/g, "");

    if(!isValid) {
      setShowSubmitErrors(true);
      return;
    }
    start();
    try {
      // Дедупликация: если уже есть идентичный объект — не создаём второй раз.
      if (cleanBin.length === 12) {
        const filter = {
          bin: { operator: "equals", operand1: cleanBin },
        };
        if (data.name) {
          filter.name = { operator: "equals", operand1: String(data.name).trim() };
        }
        if (data.addressLine) {
          filter.addressLine = {
            operator: "equals",
            operand1: String(data.addressLine).trim(),
          };
        }

        const existing = await anObjectsCRUD.search({
          paging: { skip: 0, take: 5, returnCount: true },
          filter,
          sort: { id: { operator: "desc" } },
        });
        const rows = existing?.result || [];
        if (rows.length > 0) {
          setShowSubmitErrors(true);
          setSubmitErrors("Такой объект уже создан. Пожалуйста, выберите объект из списка.");
          return;
        }
      }

      const payload = {...data};
      delete payload.subSubSectionName;
      // Только FK из справочника AnObjectSubSection (parentId = subSectionId)
      if (payload.subSubSectionId == null || payload.subSubSectionId === "") {
        delete payload.subSubSectionId;
      }

      const created = await anObjectsCRUD.create(payload);
      const full = await anObjectsCRUD.get(created?.id);
      if(afterSubmit) afterSubmit(full);
      if(onClose) onClose();
    } catch (e) {
      setSubmitErrors(e);
    } finally {
      stop();
    }
  }, [afterSubmit, data, isValid, onClose, start, stop]);

  return <Box>
    <DialogTitle>Создание объекта</DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={"2.1 Наименование объекта *"}
            onChange={e => handleChange("name", e.target.value)}
            fullWidth
            value={data.name || ""}
            required
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={"2.2 БИН/ИИН объекта *"}
            onChange={e => handleChange("bin", e.target.value)}
            fullWidth
            value={data.bin || ""}
            required
            error={!!fieldErrors.bin}
            helperText={fieldErrors.bin}
          />
          {normalizedBin.length === 12 && !isIpChecked && (
            <Typography
              variant="caption"
              color={
                gbdStatus === "verified"
                  ? "primary"
                  : gbdStatus === "loading"
                    ? "textSecondary"
                    : "secondary"
              }
              style={{ display: "block", marginTop: 4 }}
            >
              {gbdStatus === "loading" && "ГБД ЮЛ: проверяем БИН…"}
              {gbdStatus === "verified" && "ГБД ЮЛ: организация найдена"}
              {(gbdStatus === "not_found" || gbdStatus === "error") &&
                (gbdError || "ГБД ЮЛ: проверка не пройдена")}
              {gbdStatus === "idle" && "ГБД ЮЛ: ожидание проверки…"}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <CheckboxField
            label={"ИП"}
            value={isIpChecked}
            onChange={(checked) => {
              setIsIpChecked(checked);
            }}
          />

          {isIpChecked && (
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
              Отметка «ИП» влияет только на интерфейс и не сохраняется.
            </Typography>
          )}
        </Grid>
        {/*<Grid item xs={12} sm={6}>*/}
        {/*  <LegalPersonSelect*/}
        {/*    label={"2.3 Организационно-правовая форма"}*/}
        {/*    onChange={val => handleChange("legalPersonId", val)}*/}
        {/*    fullWidth*/}
        {/*  />*/}
        {/*</Grid>*/}
        <Grid item xs={12}>
          <TextField
            label={"2.4 Адрес *"}
            onChange={e => handleChange("addressLine", e.target.value)}
            fullWidth
            value={data.addressLine || ""}
            required
            error={!!fieldErrors.addressLine}
            helperText={[
              fieldErrors.addressLine,
              gbdLoading ? "Загрузка адреса из ГБД ЮЛ..." : null,
              (!fieldErrors.addressLine && !gbdLoading) ? gbdError : null,
            ].filter(Boolean).join(" ")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ObjectSectionSelect
            label={"3.1 Раздел объекта *"}
            value={data.sectionId}
            onChange={val => handleChange("sectionId", val)}
            fullWidth
            required
            error={!!fieldErrors.sectionId}
            helperText={fieldErrors.sectionId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ObjectSubSectionAutocomplete
            key={data.sectionId || "empty"}
            label={"3.2 Подраздел объекта *"}
            value={data.subSectionId}
            sectionId={data.sectionId}
            disabled={!data.sectionId}
            onChange={val => handleChange("subSectionId", val)}
            fullWidth
            required
            error={!!fieldErrors.subSectionId}
            helperText={fieldErrors.subSectionId}
          />
        </Grid>
        {showSubSubSection && (
          <Grid item xs={12} sm={6}>
            <ObjectSubSectionAutocomplete
              key={data.subSectionId || "subsub-empty"}
              label={
                hasDictSubSubSections
                  ? "3.3 Подподраздел объекта *"
                  : "3.3 Подподраздел объекта"
              }
              value={data.subSubSectionId}
              parentId={data.subSectionId}
              disabled={!data.subSectionId}
              onChange={(val) => handleChange("subSubSectionId", val)}
              fullWidth
              required={hasDictSubSubSections}
              error={!!fieldErrors.subSubSectionId}
              helperText={
                fieldErrors.subSubSectionId ||
                (!hasDictSubSubSections
                  ? "В справочнике нет подподразделов (AnObjectSubSection с parentId). Добавьте через /api/v1/anObjectSubSection."
                  : undefined)
              }
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            label={showSubSubSection ? "3.4 Объект *" : "3.3 Объект *"}
            onChange={e => handleChange("description", e.target.value)}
            value={data.description || ""}
            fullWidth
            required
            error={!!fieldErrors.description}
            helperText={fieldErrors.description}
            multiline
            rows={3}
          />
        </Grid>
        <Grid item xs={12}>
          {(submitErrors || showSubmitErrors) &&
          <Typography color={"secondary"}>
            {submitErrors || renderedErrors}
          </Typography>}
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} style={{marginLeft: "auto", marginRight: 20}}>Отменить</Button>
      <LoadingButton loading={loading} variant={"contained"} onClick={submit}>Сохранить</LoadingButton>
    </DialogActions>
  </Box>
}
